import React, { SyntheticEvent, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges.ts';
import { Profile } from 'features/profiles/useProfiles.ts';
import { BadgeModes } from 'types/badge-modes';
import { iconForAssociatedLink } from 'features/catalog/badge-associated-links.ts';
import { formatBadgeClaimPercent, isClaimPercentLowSample } from 'features/claim/badge-claim-eligibility';
import { formatBadgeObtainSubtext, orderBadgeOwnersByClaim } from 'features/claim/badge-claim-order';
import { findUniqueHighlightBadges, HIGHLIGHTS_SIZE } from 'features/catalog/badge-hash';
import { gradientFromHighlightBadges, gradientFromRarity } from 'features/catalog/badge-hash-gradient';
import { badgeIdsInJourney, JOURNEYS } from 'features/catalog/badge-journeys.ts';
import { getInitialBadgeEnabledState } from 'features/catalog/badge-state.ts';
import { relatedBadgesFor } from 'features/claim/badge-unlock-hint.ts';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { isImageCached, prefetchImage } from 'shared/lib/image-prefetch.ts';
import { contributionsPath, type ContributionsTab } from 'shared/lib/nav-paths';
import { isBattleBadge } from 'features/catalog/badge-definitions';
import { unreleasedLockLine } from 'features/catalog/badge-release';
import {
  getRarityImageUrlByName,
  getRarityScoreFromName,
  rarityOrderIndex,
  rarityPoints,
} from 'features/catalog/rarity';
import { BattleBadge } from 'features/battle/views/BadgesBattleView';
import { BadgeComponent, placeholderUrl } from 'shared/components/BadgeComponent.tsx';
import { getBadgeProperties } from 'shared/components/getBadgeProperties';
import './Modal.css';
import { ParticipantCard } from 'features/profiles/ParticipantCard.tsx';

export const getBadgeRarityName = ({ rarityName }: { rarityName: string }) => {
  const rarityScore = getRarityScoreFromName(rarityName);
  return `${rarityName.charAt(0).toUpperCase() + rarityName.slice(1)} (${rarityScore} point${
    rarityScore === rarityPoints.common ? '' : 's'
  })`;
};

interface ModalProps {
  badge: Badge;
  onClose: (openSubmitted: boolean) => void;
  setSelectedProfileID: (profileID: string) => void;
  setMode: (mode: BadgeModes) => void;
  selectedProfileID: string | null;
  mode: BadgeModes;
  onClaimThisBadge: (badge: Badge) => void;
  /** Open another badge in this modal (related-badge clicks). */
  onSelectBadge?: (badge: Badge) => void;
  /** Badge IDs owned by the signed-in user. Omit / undefined when they have no profile. */
  currentUserBadgeIDs?: string[];
  /** Profile map key (email local-part) for the signed-in user. */
  currentUserProfileId?: string | null;
}

const Modal: React.FC<ModalProps> = ({
  badge,
  onClose,
  setSelectedProfileID,
  selectedProfileID,
  setMode,
  mode,
  onClaimThisBadge,
  onSelectBadge,
  currentUserBadgeIDs,
  currentUserProfileId = null,
}) => {
  const { profilesMap, rankedProfiles, badgesMap, submissionsByEmail, feedUpdates } = useTalosData();
  const navigate = useNavigate();
  const [displaySource, setDisplaySource] = React.useState(() =>
    isImageCached(badge.imageUrl) ? badge.imageUrl : badge.smallImageUrl
  );
  const [isFullSizeReady, setIsFullSizeReady] = React.useState(() => isImageCached(badge.imageUrl));
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const asideScrollRef = useRef<HTMLDivElement>(null);
  const ownersScrollRef = useRef<HTMLDivElement>(null);
  const [isConsideredMobile, setIsConsideredMobile] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 999px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 999px)');
    const sync = () => setIsConsideredMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    mobileScrollRef.current?.scrollTo(0, 0);
    asideScrollRef.current?.scrollTo(0, 0);
    ownersScrollRef.current?.scrollTo(0, 0);
  }, [badge.id, isConsideredMobile]);

  // Progressive enhance: show thumb immediately, swap to full-size as soon as it’s ready (no artificial delay).
  // Goes through prefetchImage so full-size art stays retained after the first open.
  useEffect(() => {
    let cancelled = false;
    const fullUrl = badge.imageUrl;
    const thumbUrl = badge.smallImageUrl;

    if (isImageCached(fullUrl)) {
      setDisplaySource(fullUrl);
      setIsFullSizeReady(true);
      return;
    }

    setDisplaySource(thumbUrl);
    setIsFullSizeReady(false);

    void prefetchImage(fullUrl).then(() => {
      if (cancelled) return;
      if (isImageCached(fullUrl)) {
        setDisplaySource(fullUrl);
      }
      setIsFullSizeReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [badge.id, badge.imageUrl, badge.smallImageUrl]);

  const canClaimThisBadge = useMemo(() => {
    // No profile yet — claim flow only makes sense for existing profiles.
    if (currentUserBadgeIDs == null) return false;
    if (currentUserBadgeIDs.includes(badge.id)) return false;

    const enabledBadgesMap = getInitialBadgeEnabledState(currentUserBadgeIDs, badgesMap);
    const { disableInteraction, cursorNotAllowed } = getBadgeProperties({
      badge,
      mode: 'editing_profile',
      enabledBadgesMap,
      profileBadgeIDs: currentUserBadgeIDs,
      badgeCreditsSubmissions: [],
      badgeDesignSubmissions: [],
    });

    return !disableInteraction && !cursorNotAllowed;
  }, [badge, badgesMap, currentUserBadgeIDs]);

  const ownsThisBadge = currentUserBadgeIDs?.includes(badge.id) ?? false;

  const currentUserProfile = currentUserProfileId ? (profilesMap.get(currentUserProfileId) ?? null) : null;

  /** Claimed-by list newest-first; if you own it, you stay pinned at the top. */
  const ownersByClaim = useMemo(() => {
    const ordered = orderBadgeOwnersByClaim(
      badge.participantIDsWhoOwnThisBadge ?? [],
      badge.id,
      profilesMap,
      submissionsByEmail,
      badgesMap,
      feedUpdates
    );
    if (!ownsThisBadge || !currentUserProfileId) return ordered;
    const you = ordered.find((o) => o.profileId === currentUserProfileId);
    if (!you) return ordered;
    return [you, ...ordered.filter((o) => o.profileId !== currentUserProfileId)];
  }, [
    badge.participantIDsWhoOwnThisBadge,
    badge.id,
    profilesMap,
    submissionsByEmail,
    badgesMap,
    feedUpdates,
    ownsThisBadge,
    currentUserProfileId,
  ]);

  const rankById = useMemo(() => {
    const map = new Map<string, number>();
    rankedProfiles.forEach((p, i) => map.set(getFirstPartOfEmail(p.email), i + 1));
    return map;
  }, [rankedProfiles]);

  const youHighlightGradient = useMemo(() => {
    if (!ownsThisBadge) return null;
    if (currentUserProfile && currentUserProfileId) {
      const claimed = currentUserProfile.badgesIDs.map((id) => badgesMap.get(id)).filter((b): b is Badge => !!b);
      const hashBadges = findUniqueHighlightBadges(claimed, currentUserProfileId);
      if (hashBadges && hashBadges.length === HIGHLIGHTS_SIZE) {
        return gradientFromHighlightBadges(hashBadges, { surface: 'light' });
      }
    }
    // No Badge Hash yet — wash matching this badge’s rarity ×5.
    // Participant cards sit on the white badge modal.
    return gradientFromRarity(badge.rarityName, { surface: 'light' });
  }, [ownsThisBadge, currentUserProfile, currentUserProfileId, badgesMap, badge.rarityName]);

  const claimInviteGradient = useMemo(() => {
    if (!canClaimThisBadge) return null;
    if (currentUserProfile && currentUserProfileId) {
      const claimed = currentUserProfile.badgesIDs.map((id) => badgesMap.get(id)).filter((b): b is Badge => !!b);
      const hashBadges = findUniqueHighlightBadges(claimed, currentUserProfileId);
      if (hashBadges && hashBadges.length === HIGHLIGHTS_SIZE) {
        return gradientFromHighlightBadges(hashBadges, { surface: 'light' });
      }
    }
    return gradientFromRarity(badge.rarityName, { surface: 'light' });
  }, [canClaimThisBadge, currentUserProfile, currentUserProfileId, badgesMap, badge.rarityName]);

  /** Per-owner Badge Hash wash for Claimed-by hover (you uses youHighlightGradient). */
  const ownerHashGradients = useMemo(() => {
    const map = new Map<string, string>();
    for (const { profileId } of ownersByClaim) {
      if (ownsThisBadge && profileId === currentUserProfileId) {
        if (youHighlightGradient) map.set(profileId, youHighlightGradient);
        continue;
      }
      const profile = profilesMap.get(profileId);
      if (!profile) continue;
      const claimed = profile.badgesIDs.map((id) => badgesMap.get(id)).filter((b): b is Badge => !!b);
      if (claimed.length < HIGHLIGHTS_SIZE) continue;
      const hashBadges = findUniqueHighlightBadges(claimed, profileId);
      if (!hashBadges || hashBadges.length !== HIGHLIGHTS_SIZE) continue;
      map.set(profileId, gradientFromHighlightBadges(hashBadges, { surface: 'light' }));
    }
    return map;
  }, [ownersByClaim, ownsThisBadge, currentUserProfileId, youHighlightGradient, profilesMap, badgesMap]);

  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.keyCode === 27) {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose(false);
        }
      }
    }

    window.addEventListener('keydown', handleEscapeKey);

    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [onClose, isFullscreen]);

  const credits = badge.credits ? profilesMap.get(getFirstPartOfEmail(badge.credits)) : null;
  const designer = badge.design ? profilesMap.get(getFirstPartOfEmail(badge.design)) : null;

  const creditsTextFirstPart = `✏️ ${!badge.design ? '& 🎨 ' : ''}${credits?.name ?? badge.credits}`;
  const creditsTextSecondPart = badge.design ? `🎨 ${designer?.name ?? badge.design}` : null;
  const claimPercentLabel = formatBadgeClaimPercent(badge);
  const claimPercentLowSample = isClaimPercentLowSample(badge);

  const CreditsI = ({ part, email, tab = 'all' }: { part: string | null; email?: string; tab?: ContributionsTab }) => {
    if (!part) return null;

    const handleOnClick = (e: SyntheticEvent) => {
      e.stopPropagation();
      if (!email) return;
      const profileId = getFirstPartOfEmail(email);
      if (!profileId || !profilesMap.has(profileId)) return;
      onClose(false);
      navigate(contributionsPath(profileId, tab));
    };

    return (
      <i
        onClick={handleOnClick}
        style={{
          cursor: email && profilesMap.has(getFirstPartOfEmail(email)) ? 'pointer' : 'default',
        }}
      >
        {part}
      </i>
    );
  };

  const journeysThatAppear = JOURNEYS.filter((journey) => badgeIdsInJourney(journey).includes(badge.id)).sort(
    (a, b) => rarityOrderIndex(a.rarity) - rarityOrderIndex(b.rarity)
  );
  const relatedBadges = relatedBadgesFor(badge, badgesMap);
  const releaseLine = unreleasedLockLine(badge.id);
  const usefulLinks = releaseLine ? [] : (badge.associatedLinks ?? []);
  const hasUsefulLinks = usefulLinks.length > 0 || journeysThatAppear.length > 0;
  const hasDetailsAside = relatedBadges.length > 0;
  const showOwnersColumn = ownersByClaim.length > 0 || hasUsefulLinks || canClaimThisBadge;

  const usefulLinksBlock = hasUsefulLinks ? (
    <div className="modal-aside-block" onClick={(e: SyntheticEvent) => e.stopPropagation()}>
      <div className="badge-owners-claim-head">
        <div className="pill glow-common">Useful links</div>
      </div>
      {usefulLinks.map((link) => {
        const icon = (
          <img
            src={iconForAssociatedLink(link)}
            alt=""
            className="modal-aside-link-icon"
            onError={(e: SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.visibility = 'hidden';
            }}
          />
        );
        const label = <p className="journey-name">{link.label}</p>;
        if (link.href.startsWith('/')) {
          return (
            <Link
              key={link.href + link.label}
              className="journey-included"
              to={link.href}
              title={link.label}
              onClick={() => onClose(false)}
            >
              {icon}
              {label}
            </Link>
          );
        }
        return (
          <a
            key={link.href + link.label}
            className="journey-included"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            title={`${link.label} — ${link.href}`}
          >
            {icon}
            {label}
          </a>
        );
      })}
      {journeysThatAppear.map((journey) => (
        <div
          className="journey-included"
          key={journey.id}
          onClick={() => {
            onClose(false);
            setMode('journey_gallery');
            navigate(`/gallery/journeys#${journey.id}`);
          }}
        >
          <img
            src={getRarityImageUrlByName(journey.rarity)}
            alt={journey.rarity}
            className="journey-badge-rarity-small drop-badge-rarity-shadow"
            title={getBadgeRarityName({ rarityName: journey.rarity })}
          />
          <p className="journey-name">{journey.name}</p>
        </div>
      ))}
    </div>
  ) : null;

  const ownersColumn = showOwnersColumn ? (
    <div ref={ownersScrollRef} className="badge-owners-container">
      {usefulLinksBlock}
      {ownersByClaim.length > 0 || canClaimThisBadge ? (
        <>
          <div className="badge-owners-claim-head" onClick={(e: SyntheticEvent) => e.stopPropagation()}>
            <div className={`pill glow-${badge.rarityName}`}>
              Claimed by {ownersByClaim.length} profile
              {ownersByClaim.length === 1 ? '' : 's'} (
              {claimPercentLowSample ? (
                <>
                  {claimPercentLabel.slice(0, -1)}
                  <span className="badge-owners-claim-star">*</span>
                </>
              ) : (
                claimPercentLabel
              )}
              )
            </div>
          </div>
          {canClaimThisBadge ? (
            <button
              type="button"
              className={`participant-card variant-default modal-claim-slot participant-card--you${
                claimInviteGradient ? ' participant-card--hash' : ''
              }`}
              style={
                claimInviteGradient
                  ? ({ '--participant-hash-gradient': claimInviteGradient } as React.CSSProperties)
                  : undefined
              }
              onClick={(e: SyntheticEvent) => {
                e.stopPropagation();
                onClaimThisBadge(badge);
              }}
            >
              <span className="participant-you-tag">Unclaimed by you</span>
              <span className="modal-claim-slot-cta">Claim it</span>
            </button>
          ) : null}
          {ownersByClaim.map(({ profileId, claimedAt, wasGranted }) => {
            const profile = profilesMap.get(profileId) as Profile | undefined;
            if (!profile) return null;
            const isYou = ownsThisBadge && profileId === currentUserProfileId;
            return (
              <ParticipantCard
                key={profileId}
                profile={profile}
                badgesMap={badgesMap}
                rank={rankById.get(profileId) ?? 0}
                isYou={isYou}
                highlightGradient={ownerHashGradients.get(profileId) ?? null}
                subtext={formatBadgeObtainSubtext(wasGranted, claimedAt)}
                onClick={() => {
                  setSelectedProfileID(profileId);
                  setMode('collected');
                  onClose(false);
                }}
              />
            );
          })}
          {claimPercentLowSample ? (
            <p className="badge-owners-claim-note" onClick={(e: SyntheticEvent) => e.stopPropagation()}>
              * since added
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  ) : null;

  const handleImageClick = () => {
    setIsFullscreen(true);
  };

  const handleFullscreenClose = (e: SyntheticEvent) => {
    e.stopPropagation();
    setIsFullscreen(false);
  };

  if (isFullscreen) {
    return (
      <div className="fullscreen-overlay" onClick={handleFullscreenClose}>
        <div className="fullscreen-image-container" onClick={(e: SyntheticEvent) => e.stopPropagation()}>
          <img
            src={badge.imageUrl}
            alt={badge.name}
            className={`fullscreen-image fullscreen-image-glow-${badge.rarityName}`}
            onError={(e: SyntheticEvent<HTMLImageElement>) => {
              if (e.currentTarget.src !== placeholderUrl) e.currentTarget.src = placeholderUrl;
            }}
          />
          <button className="fullscreen-close-btn" onClick={handleFullscreenClose}>
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`modal-overlay${isConsideredMobile ? ' modal-overlay-mobile' : ''}`} onClick={() => onClose(false)}>
      <div
        ref={mobileScrollRef}
        className={isConsideredMobile ? `modal-content modal-content-mobile` : `modal-content`}
        onClick={isConsideredMobile ? (e: SyntheticEvent) => e.stopPropagation() : undefined}
      >
        {hasDetailsAside ? (
          <div
            ref={asideScrollRef}
            className="modal-aside"
            onClick={(e: SyntheticEvent) => {
              const target = e.target as HTMLElement;
              if (target.closest('a, .journey-included, .badge')) {
                e.stopPropagation();
                return;
              }
              onClose(false);
            }}
          >
            {relatedBadges.length > 0 ? (
              <div className="modal-aside-block">
                <div className="badge-owners-claim-head">
                  <div className="pill glow-common">
                    {relatedBadges.length} related badge{relatedBadges.length === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="modal-related-badges">
                  {relatedBadges.map((related) => {
                    const relatedUnreleased = unreleasedLockLine(related.id) != null;
                    return (
                      <BadgeComponent
                        key={related.id}
                        badge={related}
                        onClick={(next) => onSelectBadge?.(next)}
                        smaller
                        display
                        showName={!relatedUnreleased}
                        hiddenName={relatedUnreleased}
                        showNameHeight
                        blackAndWhite={relatedUnreleased}
                        cursorNotAllowed={relatedUnreleased}
                        showUsefulLinksOnHover={false}
                        showFloatingNameShadow={false}
                      />
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="main-and-journeys">
          <div className="main-content" onClick={(e: SyntheticEvent) => e.stopPropagation()}>
            <div className="modal-image-frame">
              <img
                src={displaySource}
                alt={badge.name}
                className="modal-image"
                onError={(e: SyntheticEvent<HTMLImageElement>) => {
                  if (e.currentTarget.src !== placeholderUrl) e.currentTarget.src = placeholderUrl;
                }}
                onClick={handleImageClick}
              />
              {isBattleBadge(badge.id) ? (
                <div className="modal-battle-peek">
                  <span className="modal-battle-peek-mark" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M5 19L10 4l3 6 6-3-5 16-3-6-6 2z" />
                      <path d="M10 10l4 4" />
                    </svg>
                  </span>
                  <div className="modal-battle-peek-card">
                    <BattleBadge badge={badge} />
                  </div>
                </div>
              ) : null}
            </div>
            {!isFullSizeReady && (
              <div
                style={{
                  marginBottom: '8px',
                  fontSize: '0.7em',
                  position: 'relative',
                }}
              >
                <i
                  style={{
                    position: 'absolute',
                    top: '-2rem',
                    left: '0.3rem',
                    color: 'white',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  Loading full-size resource...
                </i>
              </div>
            )}
            <div className="modal-badge-title">
              <img
                src={badge.rarityImageUrl}
                alt={badge.rarityName}
                className="drop-badge-rarity-shadow modal-badge-title-rarity"
                title={getBadgeRarityName(badge)}
              />
              <h3>{badge.name}</h3>
            </div>
            <p style={{ textAlign: 'center', marginBottom: '0.3rem' }}>
              {releaseLine ? <i>{releaseLine}</i> : badge.description}
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                justifyContent: 'center',
                gap: '0.6rem',
              }}
            >
              <div style={{ marginBottom: creditsTextSecondPart ? '0.1rem' : '' }}>
                <CreditsI part={creditsTextFirstPart} email={badge.credits} tab="all" />
              </div>
              <div>
                <CreditsI part={creditsTextSecondPart} email={badge.design} tab="design" />
              </div>
            </div>
          </div>
          {ownersColumn}
        </div>
      </div>
    </div>
  );
};

export default Modal;
